// Removed DashboardCard import as we're using custom styling
import { Box, Button, Text } from "@saleor/macaw-ui-next";
import React from "react";

import { HtmlPage } from "../../types";

export interface HtmlPageCardProps {
  page: HtmlPage;
  onEdit: () => void;
  onDelete: () => void;
  onToggleStatus: () => void;
  isDeleting?: boolean;
  isToggling?: boolean;
}

export const HtmlPageCard: React.FC<HtmlPageCardProps> = ({
  page,
  onEdit,
  onDelete,
  onToggleStatus,
  isDeleting = false,
  isToggling = false,
}) => {
  return (
    <Box
      style={{
        background: "white",
        borderRadius: "12px",
        border: "1px solid #e5e7eb",
        boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)",
        transition: "all 0.2s ease-in-out",
        marginBottom: "16px"
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = "0 4px 6px rgba(0, 0, 0, 0.1), 0 2px 4px rgba(0, 0, 0, 0.06)";
        e.currentTarget.style.transform = "translateY(-1px)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = "0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)";
        e.currentTarget.style.transform = "translateY(0)";
      }}
    >
      <Box padding={6}>
        {/* Header Section */}
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" marginBottom={4}>
          <Box __flex="1" __minWidth="0" marginRight={4}>
            {/* Page Name and Status */}
            <Box display="flex" alignItems="center" gap={3} marginBottom={3}>
              <Text size={5} fontWeight="bold" as="h3" color="default1">
                {page.name}
              </Text>
              <Box 
                paddingX={3}
                paddingY={1}
                style={{
                  backgroundColor: page.isActive ? "#dcfce7" : "#f3f4f6",
                  borderRadius: "20px",
                  border: `1px solid ${page.isActive ? "#16a34a" : "#d1d5db"}`
                }}
              >
                <Text 
                  size={1} 
                  fontWeight="bold"
                  style={{ 
                    color: page.isActive ? "#15803d" : "#6b7280" 
                  }}
                >
                  {page.isActive ? "ACTIVE" : "INACTIVE"}
                </Text>
              </Box>
            </Box>

            {/* Page Type */}
            {page.type && (
              <Box marginBottom={3}>
                <Box 
                  paddingX={3}
                  paddingY={1}
                  style={{
                    backgroundColor: "#f1f5f9",
                    borderRadius: "8px",
                    border: "1px solid #cbd5e1",
                    display: "inline-block"
                  }}
                >
                  <Text size={1} color="default2" fontWeight="medium">
                    Type: {page.type.trim()}
                  </Text>
                </Box>
              </Box>
            )}
            
            {/* Description */}
            {page.description && (
              <Box marginBottom={4}>
                <Text color="default2" size={3} __lineHeight="1.6">
                  {page.description}
                </Text>
              </Box>
            )}
            
            {/* Frontend URL */}
            {page.slug && (
              <Box 
                marginBottom={4}
                padding={3}
                style={{
                  backgroundColor: "#f8fafc",
                  borderRadius: "8px",
                  border: "1px solid #e2e8f0"
                }}
              >
                <Text size={1} color="default2" marginBottom={1} fontWeight="medium">
                  Frontend URL
                </Text>
                <Text 
                  size={3} 
                  fontWeight="bold" 
                  style={{ 
                    fontFamily: "monospace",
                    color: "#2563eb"
                  }}
                >
                  /{page.slug}
                </Text>
              </Box>
            )}

            {/* Metadata */}
            <Box 
              display="flex" 
              gap={6}
              paddingTop={3}
              style={{
                borderTop: "1px solid #f1f5f9"
              }}
            >
              <Box>
                <Text size={1} color="default2" marginBottom={1} fontWeight="medium">
                  Created
                </Text>
                <Text size={2} fontWeight="medium">
                  {new Date(page.createdAt).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric'
                  })}
                </Text>
              </Box>
              <Box>
                <Text size={1} color="default2" marginBottom={1} fontWeight="medium">
                  Last Updated
                </Text>
                <Text size={2} fontWeight="medium">
                  {new Date(page.updatedAt).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric'
                  })}
                </Text>
              </Box>
            </Box>
          </Box>

          {/* Action Buttons - Right Side */}
          <Box display="flex" gap={2} alignItems="flex-start">
            <Button 
              variant="secondary" 
              size="small" 
              onClick={onEdit}
              disabled={isDeleting || isToggling}
            >
              Edit
            </Button>
            <Button
              variant={page.isActive ? "secondary" : "primary"}
              size="small"
              onClick={onToggleStatus}
              disabled={isDeleting || isToggling}
            >
              {isToggling 
                ? (page.isActive ? "Deactivating..." : "Activating...")
                : (page.isActive ? "Deactivate" : "Activate")
              }
            </Button>
            <Button 
              variant="error" 
              size="small" 
              onClick={onDelete}
              disabled={isDeleting || isToggling}
            >
              {isDeleting ? "Deleting..." : "Delete"}
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};